import * as debounce from 'lodash.debounce'
import * as loadjs from 'loadjs'
import { lazyTimeout } from '../constants'
import { ExperimentFlag } from '../enums/ExperimentFlag'
import { LoadJsBundle } from '../enums/LoadJsBundle'
import { Device, WindowSize } from '../helpers'
import { Scroll } from '../helpers/Scroll'
import PrebidFacade from '../prebidFacade/PrebidFacade'
import { Slot } from '../slots/Slot'
import SlotRepository from '../slots/SlotRepository'
import { addClass } from '../helpers/DOM'

export const TRIGGER_SUFFIX = 'waypoint'
export const DEFAULT_MOBILE_OFFSET = 2400
export const DEFAULT_DESKTOP_OFFSET = 1600

const cleanupMultiplier = 12
const cleanupHeight = WindowSize.height * cleanupMultiplier
export let maxVelocity

export default class Lazyable {
  // These statics let us debounce bid requests from lazy slots.
  static lazySlotsToRequest: Slot[] = []
  static debounceRequestBids: Function

  // remove ad after it's 7 window-heights above the viewPort.
  static cleanupObserver = new IntersectionObserver(cleanupCallback, {
    rootMargin: `${cleanupHeight}px 0px 0px 0px`
  })

  static lazyLoadObservers: IntersectionObserver[] = []

  observer: IntersectionObserver
  cleanupObserver?: IntersectionObserver
  scrollUpObserver?: IntersectionObserver

  constructor(private slot: Slot, private prebidFacade: PrebidFacade) {
    this.createWaypoint()

    // Set up the static Lazyable.debounceRequestBids if it doesn't already exist
    if (!Lazyable.debounceRequestBids) {
      Lazyable.debounceRequestBids = debounce(
        () => {
          // Let's find the largest timeout among the slots, and use that.
          const timeouts = Lazyable.lazySlotsToRequest.map(
            (slot) => slot.timeout || lazyTimeout()
          )
          const maxTimeout = Math.max(...timeouts)

          prebidFacade.requestBids(Lazyable.lazySlotsToRequest, maxTimeout)
          Lazyable.lazySlotsToRequest = []
        },
        100,
        { maxWait: 500 }
      )
    }
  }

  createWaypoint(): void {
    const slot: Slot = this.slot

    const offset = slot.offset
      ? slot.offset
      : Device.isMobileOrTablet
      ? DEFAULT_MOBILE_OFFSET
      : DEFAULT_DESKTOP_OFFSET

    // Different slots have different offsets, so here we see if we've created an
    // IntersectionObserver for a particular offset value, and create it if it doesn't exist.
    let lazyLoadObserver = Lazyable.lazyLoadObservers[slot.offset]
    if (!lazyLoadObserver) {
      const options = {
        rootMargin: `0px 0px ${offset}px 0px`
      }

      lazyLoadObserver = new IntersectionObserver(lazyLoadCallback, options)
      Lazyable.lazyLoadObservers[offset] = lazyLoadObserver
    }

    const observedEl = this.slot.wrapper
    lazyLoadObserver.observe(observedEl)
  }

  /**
   * This creates a waypoint that will remove an ad from the DOM after it has been scrolled past
   * by a particular amount.  This should help performance on very long pages.
   */
  createCleanupWaypoint(): void {
    const slot: Slot = this.slot
    if (slot.wrapper) {
      Lazyable.cleanupObserver.observe(slot.wrapper)
    }
  }
}

/**
 * IntersectionObserver callback that that lazy-loads ads as the user scrolls close enough to them.
 * @param entries
 * @param observer
 */
function lazyLoadCallback(
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver
): void {
  const velocity = Scroll.delta

  entries.forEach((entry) => {
    const slotId = entry.target.getAttribute('data-wrapper') || ''
    const slot = SlotRepository.getSlotById(slotId)

    if (!slot) {
      return
    }
    maxVelocity = getMaxVelocity(slot)

    if ((entry.isIntersecting || slot.sticky) && velocity < maxVelocity) {
      // console.log('lazyLoadCallback', slotId, entry)
      observer.unobserve(entry.target)
      loadjs.ready(LoadJsBundle.nonLazyRequestsLoaded, () => {
        // Wait for NonLazy Ads to have loaded before requesting new bids.
        slot.setTargeting({ velocity, maxVelocity })
        Lazyable.lazySlotsToRequest.push(slot)
        Lazyable.debounceRequestBids()
        // Setup cleanup observer
        Lazyable.cleanupObserver.observe(slot.wrapper)
      })
    }
  })
}

/**
 * IntersectionObserver callback that removes ads from the DOM if the user scrolls too far past it.
 * @param entries
 * @param observer
 */
function cleanupCallback(
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver
): void {
  entries.forEach((entry) => {
    const slotId = entry.target.getAttribute('data-wrapper')
    const isAboveViewport = entry.boundingClientRect.bottom < 0

    // Sanity check. It helps one site (https://www.bucketlistly.blog/photos),
    // where Content-hints are used in a modal, work correctly.
    // Things might get weird when with modals / fixed positioning?
    const isAboveCleanupHeight =
      entry.boundingClientRect.bottom <= -cleanupHeight

    if (
      slotId &&
      isAboveViewport &&
      isAboveCleanupHeight &&
      entry.intersectionRatio === 0
    ) {
      observer.unobserve(entry.target)
      // console.log('cleanupCallback', slotId)
      const slot = SlotRepository.getSlotById(slotId)
      if (slot) {
        slot.destroyGSlot()

        const wrapperDiv = slot.wrapper as HTMLDivElement

        // https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-anchor/Guide_to_scroll_anchoring
        // Browsers that support Scroll Anchoring can smoothly handle elements
        // above the viewport being added/removed. Browsers that don't support
        // it end up jankily moving the viewport when elements above the
        // viewport are added/removed.
        if (Device.supportsScrollAnchor) {
          addClass(wrapperDiv, 'mv-empty-wrapper')
        } else {
          // Looks worse if we scroll back up the hidden element, but prevents janky scroll
          wrapperDiv.style.visibility = 'hidden'
        }
      }
    }
  })
}

function getMaxVelocity(slot: Slot): number {
  return (
    maxVelocity ||
    (parseInt(
      slot.model.experiment.activate(ExperimentFlag.velocityCheck) || '0',
      10
    ) ||
      1000)
  )
}
